import { create } from 'zustand';

import { closeOtherDrawers, registerDrawerCloser } from './_drawer-mutex';

import { useInspector } from '@/components/ui/inspector/hooks/useInspector';
import type { InspectorBreadcrumbItem } from '@/components/ui/inspector/types';
import { registerWorkspaceReset } from '@/lib/workspace/workspaceResetRegistry';

export interface DocumentDialogPayload {
  id: string;
  title: string;
  markdown: string;
  /** One of ARTIFACT_TYPES (src/lib/ai-agent/tools/generate-document.ts), or 'document' as a legacy/unknown fallback. */
  type?: string;
  wordCount?: number;
  sessionId?: string;
  messageId?: string;
  updatedAt?: string;
  sizeKB?: number;
  lineCount?: number;
  /** Editorial source identity enables the document review rail. */
  editorialTopicId?: string;
  /**
   * Opens the exact quoted passage in the editor; instruction preloads the AI rewrite request. `requestId` is one
   * per press of the button: pressing "Fix with AI" again for the same finding is a new request, so the passage is
   * selected again and the bar reopens even when the first request was dismissed or already used.
   */
  reviewAction?: { quote: string; instruction?: string; requestId?: number };
  /**
   * Set when this payload is a saved revision other than the article's
   * current one (e.g. the article version browser). Renders a banner so a
   * stale revision can never be mistaken for the live article — the
   * north-star rule that nothing may falsely pass.
   */
  historicalNotice?: string;
  /**
   * The live article, so a reader who opened an old version has a way back.
   * Without it the only exit was closing the viewer and re-opening the topic.
   */
  currentVersion?: DocumentDialogPayload;
  /**
   * This document is a saved revision of `editorialTopicId` that the reader can
   * make current — offered in the viewer, after it has been read, rather than
   * from the list where it was only a title and a date.
   */
  promotable?: boolean;
}

interface DocumentDialogState {
  isOpen: boolean;
  payload: DocumentDialogPayload | null;
  /**
   * The review note whose card in the article's review rail was pressed, so the reader can scroll to
   * its passage and strengthen that highlight. Optional: only the document variant of the rail sets
   * it, and `focusRequestId` changes on every press so pressing the same card again scrolls again.
   */
  focusedFindingKey?: string | null;
  focusRequestId?: number;
  focusFinding: (key: string | null) => void;
  onCopy?: () => void | Promise<void>;
  onDownload?: () => void | Promise<void>;
  onOpenInChat?: () => void;
  onClose?: () => void;
  open: (args: {
    document: DocumentDialogPayload;
    breadcrumbs?: InspectorBreadcrumbItem[];
    onCopy?: () => void | Promise<void>;
    onDownload?: () => void | Promise<void>;
    onOpenInChat?: () => void;
    onClose?: () => void;
  }) => void;
  close: () => void;
}

/**
 * Document dialog payload carrier. Mirrors useImageDialogStore — calling
 * `open()` here pushes a document subject onto the Inspector store so the
 * GlobalInspectorMount renders DocumentBody.
 *
 * Closing via `close()` clears both stores. The Inspector's own close
 * gestures bridge through GlobalInspectorMount onOpenChange, which calls
 * `close()` here when the active subject kind is 'document'. The optional
 * `onClose` callback fires (used by callers that need to clean up URL
 * params, e.g. DocumentsListView).
 */
export const useDocumentDialogStore = create<DocumentDialogState>((set, get) => ({
  isOpen: false,
  payload: null,
  focusedFindingKey: null,
  focusRequestId: 0,
  focusFinding: (key) =>
    set((state) => ({ focusedFindingKey: key, focusRequestId: (state.focusRequestId ?? 0) + 1 })),
  onCopy: undefined,
  onDownload: undefined,
  onOpenInChat: undefined,
  onClose: undefined,
  open: ({ document, breadcrumbs, onCopy, onDownload, onOpenInChat, onClose }) => {
    // RC-8 mutex: close every other drawer first so we never stack.
    // Documents now share the workspace viewer with media so library/chat
    // opens feel like one page-like inspection surface, not a legacy popup.
    closeOtherDrawers('document');
    set({
      isOpen: true,
      payload: document,
      focusedFindingKey: null,
      onCopy,
      onDownload,
      onOpenInChat,
      onClose,
    });
    const inspectorOptions = breadcrumbs
      ? { presentation: 'workspace' as const, breadcrumbs }
      : { presentation: 'workspace' as const };
    useInspector
      .getState()
      .openSubject({ kind: 'document', messageId: document.id }, inspectorOptions);
  },
  close: () => {
    const { onClose } = get();
    set({
      isOpen: false,
      payload: null,
      focusedFindingKey: null,
      onCopy: undefined,
      onDownload: undefined,
      onOpenInChat: undefined,
      onClose: undefined,
    });
    onClose?.();
    const inspector = useInspector.getState();
    const top = inspector.stack[inspector.stack.length - 1];
    if (top?.kind === 'document') inspector.close();
  },
}));

registerDrawerCloser('document', () => useDocumentDialogStore.getState().close());

if (typeof window !== 'undefined' && import.meta.env.DEV) {
  (
    window as unknown as { __documentDialogStore: typeof useDocumentDialogStore }
  ).__documentDialogStore = useDocumentDialogStore;
}

// Same reasoning as useVideoDialogStore's registration: `payload` here is a
// real document carried over from whichever workspace was active when it was
// opened, and nothing else clears it on a workspace switch. Force the
// carrier empty directly (not close(), which also fires onClose/onCopy/
// onDownload callbacks meant for a user-initiated close, not a workspace
// teardown).
registerWorkspaceReset('document-dialog-store', () => {
  useDocumentDialogStore.setState({
    isOpen: false,
    payload: null,
    onCopy: undefined,
    onDownload: undefined,
    onOpenInChat: undefined,
    onClose: undefined,
  });
});
